import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from "@mui/styles";
import { styled } from '@mui/material/styles';
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, InputAdornment, IconButton, Step, Stepper, StepContent, StepLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Button, Tooltip, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Eye, EyeOff, X } from 'lucide-react';

import FlagstoneTheme from '../../../../Config/flagstoneTheme';
import { searchTypes } from '../../../../Config/constants';
import { dataService } from '../../../../Services/data/data.service';

export default function MetisUserDialog({ dialogOpen, closeDialog, editing, selectedUser, allUsers, allUserRoles, allUserFeatures, setUpdatingMetisUsers, addMetisUserLocally, updateMetisUserLocally }) {

    const classes = useStyles();
    const { t } = useTranslation();

    // User data + errors
    const [name, setName] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [badgeNumber, setBadgeNumber] = useState("");
    const [badgeNumberInUse, setBadgeNumberInUse] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");

    const [newPassword, setNewPassword] = useState("");
    const [oldPassword, setOldPassword] = useState("");

    const [isScanning, setIsScanning] = useState(false);
    const [editingPassword, setEditingPassword] = useState(false);

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [passwordConfirmationVisible, setPasswordConfirmationVisible] = useState(false);


    const [nameError, setNameError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [passwordConfirmationError, setPasswordConfirmationError] = useState(false);

    const [newPasswordError, setNewPasswordError] = useState(false);
    const [oldPasswordError, setOldPasswordError] = useState(false);


    const [selectedUserRoles, setSelectedUserRoles] = useState([]);
    const [selectedUserFeatures, setSelectedUserFeatures] = useState([]);

    // barcode button
    const [hovered, setHovered] = useState(false);

    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && selectedUser) {
            setName(selectedUser.Name);

            // Set the active roles for the current user
            let selectedRoles = [];
            selectedUser.Roles.forEach(role => {
                selectedRoles.push(allUserRoles.find(r => r.Id === role.Id));
            });
            setSelectedUserRoles(selectedRoles);
            // Set the active features for the current user
            let selectedFeatures = [];
            selectedUser.Features.forEach(feature => {
                selectedFeatures.push(allUserFeatures.find(f => f.Id === feature.Id));
            });
            setSelectedUserFeatures(selectedFeatures);
        }
    }, [selectedUser])

    const handleScanBarcode = () => {
        setBadgeNumber("");
        setIsScanning(true);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }

    const processBarcode = (e) => {
        if (e.key === "Enter") {
            setHovered(false);
            inputRef.current.blur();
            console.log(inputRef.current.value)

            let selectedUserId = 0;

            if(selectedUser !== undefined) {
                selectedUserId = selectedUser.Id;
            }

            dataService.CheckIfBadgeNumberIsFree(inputRef.current.value, selectedUserId).then((response) => {
                console.log(response)
                console.log(response.data)
                console.log(response.data.Succeeded)
                if (response.ok) {
                    if(response.data.Succeeded)
                    {
                        setBadgeNumberInUse(false);
                    } else {
                        setBadgeNumberInUse(true);
                    }
                } else {
                    setBadgeNumberInUse(true);
                    console.log(true)
                }
            })

            setBadgeNumber(e.target.value);
            setIsScanning(false);
            inputRef.current.value = "";
        }
    }

    const handleRoleSelection = (e) => {
        // if checked, add to selected roles
        if (e.target.checked) {
            // add entire role object to setSelectedUserRoles
            setSelectedUserRoles([...selectedUserRoles, allUserRoles.find(role => role.Id.toString() === e.target.value)]);
        } else {
            // if unchecked, remove from selected roles
            setSelectedUserRoles(selectedUserRoles.filter(role => role.Id.toString() !== e.target.value));
        }
    };

    const handleFeatureSelection = (e) => {
        // if checked, add to selected features
        if (e.target.checked) {
            // add entire feature object to setSelectedUserFeatures
            setSelectedUserFeatures([...selectedUserFeatures, allUserFeatures.find(feature => feature.Id.toString() === e.target.value)]);
        } else {
            // if unchecked, remove from selected features
            setSelectedUserFeatures(selectedUserFeatures.filter(feature => feature.Id.toString() !== e.target.value));
        }
    };

    const cancel = () => {
        closeDialog();
    }

    const submitUser = () => {
        if (editing) {
            validateUserUpdate();
        } else {
            createUser();
        }
    }

    const createUser = () => {
        let user = {
            Name: name,
            Roles: selectedUserRoles,
            Features: selectedUserFeatures,
            BadgeNumber: badgeNumber,
            Password: password,
            PasswordConfirmation: passwordConfirmation
        }

        if (validateUser(user)) {
            dataService.createMetisUser(user).then((response) => {
                if (response.ok) {
                    addMetisUserLocally(JSON.parse(JSON.parse(response.data.Messages[0]).Data).user);
                    closeDialog();
                    toast.success(t("User created successfully"), { position: "top-center" });
                } else {
                    toast.error(t("Error creating user") + response.data, { position: "top-center" });
                }
            })
        }
    }

    const updateUser = (user) => {
        dataService.updateMetisUser(user).then((response) => {
            if (response.ok) {
                updateMetisUserLocally(user);
                closeDialog();
                toast.success(JSON.parse(JSON.parse(response.data.Messages[0]).Data).Message, { position: "top-center" });
            } else {
                toast.error(t("Error updating user") + response.data, { position: "top-center" });
            }
        })
    }

    const resetErrors = () => {
        setNameError(false);
        // setBadgeNumberError(false);
        setPasswordError(false);
        setPasswordConfirmationError(false);
    }

    const checkUsernameAvailability = (name) => {
        let user = allUsers.find(u => u.Name.toLowerCase().trim() === name.toLowerCase().trim());
        if (user) {
            return true;
        } else {
            return false;
        }
    }

    const validateUser = (user) => {
        let errors = 0;
        resetErrors();

        if (user.Password !== user.PasswordConfirmation) {
            setPasswordConfirmationError(t("Passwords do not match"));
            errors++;
        }
        if (!user.Password) {
            setPasswordError(t("Password is required"));
            errors++;
        }
        if (!user.Name) {
            setNameError(t("Name is required"));
            errors++;
        }
        // if (!user.badgeNumber) {
        //     setBadgeNumberError("Badge number is required");
        //     errors++;
        // }

        console.log(badgeNumberInUse)
        if(badgeNumberInUse){
            setNameError(t("Badge already in use"));
            errors++;
        }

        if (checkUsernameAvailability(user.Name)) {
            setNameError(t("Username is already in use"));
            errors++;
        }

        if (errors > 0) {
            return false;
        } else {
            return true;

        }
    }

    const validateUserUpdate = () => {
        let errors = 0;

        let tempBadgeNumber = "";

        if (badgeNumber == "") {
            if (selectedUser.Badgenumber != "") {
                tempBadgeNumber = selectedUser.Badgenumber
            }
        } else {
            tempBadgeNumber = badgeNumber;
        }

        let user = {
            Id: selectedUser.Id,
            Name: name,
            Roles: selectedUserRoles,
            Features: selectedUserFeatures,
            Badgenumber: tempBadgeNumber,
            Password: newPassword,
        }

        resetErrors();

        let oldPasswordCorrect = false;

        if (oldPassword.trim() != "" && oldPassword !== selectedUser.Password) {
            setOldPasswordError(t("Incorrect password"));
            oldPasswordCorrect = true;
            errors++;
        } else {
            setEditingPassword(true);
        }

        if (oldPasswordCorrect && newPassword.trim() == "") {
            setNewPasswordError(t("Enter a new password"));
            errors++;
        } else if (oldPasswordCorrect && newPassword.trim() != "") {
            setPassword(newPassword);
        }

        if (oldPassword.trim() == "" && newPassword.trim() !== "") {
            setNewPasswordError(t("Enter old password"));
            errors++;
        }

        console.log(badgeNumberInUse)
        if(badgeNumberInUse){
            setNameError(t("Badge already in use"));
            errors++;
        }
        // if (newPassword.toString().trim() == "") {
        //     user.Password = selectedUser.Password
        // }
        // if (editingPassword) {
        //     if (user.password !== user.passwordConfirmation) {
        //         setPasswordConfirmationError(t("Passwords do not match"));
        //         errors++;
        //     }
        //     if (!user.password) {
        //         setPasswordError(t("Password is required"));
        //         errors++;
        //     }
        // }


        if (!user.Name) {
            setNameError(t("Name is required"));
            errors++;
        }

        if (selectedUser.Name !== user.Name) {
            if (checkUsernameAvailability(user.Name)) {
                setNameError(t("Username is already in use"));
                errors++;
            }
        }

        if (errors > 0) {
            return false;
        } else {
            updateUser(user);
        }
    }

    const startEditingPassword = () => {
        if (password === selectedUser.Password) {
            setPassword("");
            setPasswordConfirmation("");
            setPasswordError(false);
            setPasswordConfirmationError(false);

            setEditingPassword(true);
        } else {
            setPasswordError(t("Incorrect password"));
        }
    }

    const FlagstoneSwitch = styled((props) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 38,
        height: 20,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 1,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(18px)',
                color: FlagstoneTheme.palette.primary.main,
                '& + .MuiSwitch-track': {
                    backgroundColor: '#EDF2FF',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: FlagstoneTheme.palette.primary.main,
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 18,
            height: 18,
        },
        '& .MuiSwitch-track': {
            borderRadius: 20 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    }));

    return (

        <Dialog className={classes.dialog}
            open={dialogOpen}
            onClose={closeDialog}

            classes={{ paper: classes.dialogPaper }}

            aria-labelledby="draggable-dialog-title"
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {editing ? t("Edit user - Metis") : t("Add user - Metis")}
                </DialogTitle>
                <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    <div className={classes.dialog__form}>
                        <div className={classes.form__row}>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label} style={{ color: nameError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{nameError ? nameError : t("Username")}</label>
                                <input className={classes.form__input} style={{ borderColor: nameError ? "#D84949" : "#A9A9A9" }} type="text" value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                        </div>
                        <div className={classes.form__badgeContainer}>
                            {editing ?
                                selectedUser.Badgenumber == "" && badgeNumber == "" ?
                                    <button onClick={() => handleScanBarcode()} className={`${classes.form__noBadgeButton} ${isScanning ? classes.scanning : ''}`}>
                                        {isScanning ? t("Scan your badge now") : t("Assign a badge")}
                                    </button>
                                    :
                                    badgeNumber == "" ?
                                        isScanning ?
                                            <button onClick={() => handleScanBarcode()} className={`${classes.form__noBadgeButton} ${classes.scanning}`}>
                                                {t("Scan your badge now")}
                                            </button>
                                            :
                                            <button
                                                onClick={() => handleScanBarcode()}
                                                className={classes.form__editBadgeButton}
                                                onMouseEnter={() => setHovered(true)}
                                                onMouseLeave={() => setHovered(false)}
                                            >
                                                {hovered ? t("Scan new badge") : t("This user has a badge")}
                                            </button>
                                        :
                                        <button
                                            onClick={() => handleScanBarcode()}
                                            className={classes.form__badgeButton}
                                            onMouseEnter={() => setHovered(true)}
                                            onMouseLeave={() => setHovered(false)}
                                        >
                                            {hovered ? t("Scan again") : badgeNumber}
                                        </button>
                                :
                                badgeNumber == "" ?
                                    <button onClick={() => handleScanBarcode()} className={`${classes.form__noBadgeButton} ${isScanning ? classes.scanning : ''}`}>
                                        {isScanning ? t("Scan your badge now") : t("Assign a badge")}
                                    </button>
                                    :
                                    <button
                                        onClick={() => handleScanBarcode()}
                                        className={classes.form__badgeButton}
                                        onMouseEnter={() => setHovered(true)}
                                        onMouseLeave={() => setHovered(false)}
                                    >
                                        {hovered ? 'Scan again' : badgeNumber}
                                    </button>
                            }
                            <input type="text" tabIndex={"-1"} style={{ opacity: "0", height: "0px", margin: "0px", padding: "0px", border: "none", position: "absolute" }} onBlur={() => setIsScanning(false)} autoComplete='off' onKeyDown={(e) => processBarcode(e)} name="barcode" id="barcode" ref={inputRef} />
                        </div>
                        <div className={classes.form__row}>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label}>{t("Roles")}</label>
                                <div className={classes.form__roles}>
                                    {allUserRoles.map((role, index) => {
                                        return (
                                            <div className={classes.form__roleContainer} key={index}>
                                                <input checked={selectedUserRoles.some(selectedRole => selectedRole.Id === role.Id)} type="checkbox" id={role.Id} name={role.DisplayName} value={role.Id} onChange={(e) => handleRoleSelection(e)} />
                                                <Tooltip title={role.DisplayName} placement='top' disableInteractive
                                                    slotProps={{
                                                        popper: {
                                                            modifiers: [
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, -14],
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                    }}>
                                                    <label for={role.Id}>{role.DisplayName}</label>
                                                </Tooltip>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* <div className={classes.form__switchContainer}>
                                    <FormControlLabel
                                        className={classes.form__switchLabel}
                                        control={<FlagstoneSwitch checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />}
                                        label="Admin"
                                    />
                                </div> */}
                            </div>
                        </div>
                        <div className={classes.form__row}>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label}>{t("Features")}</label>
                                <div className={classes.form__roles}>
                                    {allUserFeatures.map((feature, index) => {
                                        return (
                                            <div className={classes.form__roleContainer} key={index}>
                                                <input checked={selectedUserFeatures.some(selectedFeature => selectedFeature.Id === feature.Id)} type="checkbox" id={feature.Id} name={feature.DisplayName} value={feature.Id} onChange={(e) => handleFeatureSelection(e)} />
                                                <Tooltip title={feature.displayName} placement='top' disableInteractive
                                                    slotProps={{
                                                        popper: {
                                                            modifiers: [
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, -14],
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                    }}>
                                                    <label for={feature.Id}>{feature.DisplayName}</label>
                                                </Tooltip>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className={classes.form__row}>
                            {editing ?
                                <>
                                    <div className={classes.form__inputContainer}>
                                        <label className={classes.form__label} style={{ color: oldPasswordError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{oldPasswordError ? oldPasswordError : t("Old password")}</label>
                                        <div className={classes.form__passwordInputContainer}>
                                            <input className={classes.form__passwordInput} style={{ borderColor: oldPasswordError ? "#D84949" : "#A9A9A9" }} type={passwordVisible ? "text" : "password"} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                                            <button onClick={() => setPasswordVisible(!passwordVisible)} className={classes.form__showPasswordButton}>
                                                {passwordVisible ?
                                                    <EyeOff size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                    :
                                                    <Eye size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <div className={classes.form__inputContainer}>
                                        <label className={classes.form__label} style={{ color: newPasswordError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{newPasswordError ? newPasswordError : t("New password")}</label>
                                        <div className={classes.form__passwordInputContainer}>
                                            <input className={classes.form__passwordInput} style={{ borderColor: newPasswordError ? "#D84949" : "#A9A9A9" }} type={passwordConfirmationVisible ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                            <button onClick={() => setPasswordConfirmationVisible(!passwordConfirmationVisible)} className={classes.form__showPasswordButton}>
                                                {passwordConfirmationVisible ?
                                                    <EyeOff size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                    :
                                                    <Eye size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </>
                                :
                                <>
                                    <div className={classes.form__inputContainer}>
                                        <label className={classes.form__label} style={{ color: passwordError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{passwordError ? passwordError : t("Password")}</label>
                                        <div className={classes.form__passwordInputContainer}>
                                            <input className={classes.form__passwordInput} style={{ borderColor: passwordError ? "#D84949" : "#A9A9A9" }} type={passwordVisible ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                                            <button onClick={() => setPasswordVisible(!passwordVisible)} className={classes.form__showPasswordButton}>
                                                {passwordVisible ?
                                                    <EyeOff size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                    :
                                                    <Eye size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <div className={classes.form__inputContainer}>
                                        <label className={classes.form__label} style={{ color: passwordConfirmationError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{passwordConfirmationError ? passwordConfirmationError : t("Confirm password")}</label>
                                        <div className={classes.form__passwordInputContainer}>
                                            <input className={classes.form__passwordInput} style={{ borderColor: passwordConfirmationError ? "#D84949" : "#A9A9A9" }} type={passwordConfirmationVisible ? "text" : "password"} value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} />
                                            <button onClick={() => setPasswordConfirmationVisible(!passwordConfirmationVisible)} className={classes.form__showPasswordButton}>
                                                {passwordConfirmationVisible ?
                                                    <EyeOff size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                    :
                                                    <Eye size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </DialogContent >
            <DialogActions className={classes.dialog__buttonContainer}>
                <Button className={classes.dialog__button__secondary} onClick={() => cancel()}>
                    {t('Cancel')}
                </Button>
                <Button className={classes.dialog__button} onClick={() => submitUser()}>
                    {editing ? t('Save') : t('Create user')}
                </Button>
            </DialogActions>
        </Dialog >
    );
}

const useStyles = makeStyles(() => ({

    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '400px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },

    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "24px",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    dialog__form: {
        width: "100%",

        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__row: {
        width: "100%",
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__badgeContainer: {
        width: '100%',
        margin: "4px 0px",
    },

    form__noBadgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: '#0C0E29',

        backgroundColor: '#F8F8F8',
        border: 'none',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #EDEDED",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    scanning: {
        backgroundImage: 'linear-gradient(90deg, #F3FBF2 25%, #E2F8E0 50%, #F3FBF2 75%)',
        backgroundSize: '200% 100%',
        border: '1px solid #E2F8E0',
        animation: '$moveGradient 2s linear infinite',
        animationTimingFunction: 'ease-in-out',
        animationDelay: '0.5s',

        '&:hover': {
            backgroundImage: 'linear-gradient(90deg, #F3FBF2 25%, #E2F8E0 50%, #F3FBF2 75%)',
            backgroundSize: '200% 100%',
            border: '1px solid #E2F8E0',
            animation: '$moveGradient 2s linear infinite',
            animationTimingFunction: 'ease-in-out',
            animationDelay: '0.5s',
            color: FlagstoneTheme.palette.text.primary,
        },
    },
    '@keyframes moveGradient': {
        '0%': {
            backgroundPosition: '200% 0%',
        },
        '100%': {
            backgroundPosition: '-200% 0%',
        },
    },

    form__badgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: FlagstoneTheme.palette.primary.main,

        backgroundColor: '#EDF2FF',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #C3D3FF",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    form__editBadgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: FlagstoneTheme.palette.text.primary,

        backgroundColor: '#F3FBF2',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #C6EFC3",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    form__inputContainer: {
        width: '100%',

        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    form__switchContainer: {
        width: '100%',
        height: "40px",

        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },

    form__roles: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        flexWrap: 'wrap',
    },

    form__roleContainer: {
        width: "calc(50% - 4px)",
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        alignItems: 'center',

        // label ellipsis
        '& label': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',

            // disable text selection
            userSelect: 'none',
        }
    },

    form__passwordInputContainer: {
        position: 'relative',
    },

    form__inputFieldContainer: {
        position: 'relative',
    },

    form__passwordInput: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    form__showPasswordButton: {
        height: "32px",
        width: "32px",
        borderRadius: "4px",

        backgroundColor: "transparent",

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '4px',

        cursor: 'pointer',
        border: 'none',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#EDEDED',
        },
    },

    form__switchLabel: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,

        gap: "8px !important",
        margin: "0px !important",
    },

    form__label: {
        fontSize: '12px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,
    },

    form__input: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    form__inputEndAdornment: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '8px',
    },

    dialog__inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    dialog__buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between !important',
        width: "100% !important",
        boxSizing: 'border-box',

        padding: '16px !important',
        paddingTop: '0px !important',
        margin: '0px !important',
    },

    dialog__button__secondary: {
        color: FlagstoneTheme.palette.primary.main + " !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: "white !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: "none !important",

        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
            color: "white !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    buttons: {
        textAlign: "center"
    },
    stepButton: {
        zIndex: 1,
        color: "#fff",
        width: 50,
        height: 50,
        display: "flex",
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
    },

    dialog__button: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: "none !important",

        // align right
        marginLeft: 'auto !important',

        '&:hover': {
            backgroundColor: "white !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },
}));
